import React, { ReactNode, useState } from 'react';
import { AlertTriangleIcon } from '../SvgIconsV3/AlertTriangle';
import { CircleCheckIcon } from '../SvgIconsV3/CircleCheck';
import { CircleXIcon } from '../SvgIconsV3/CircleX';
import { InfoCircleIcon } from '../SvgIconsV3/InfoCircle';
import { CreateIconComponentProps } from '../SvgIcons/CreateIconComponent';
import { ExclusiveAnd, Either } from '../../util/types';
import { COLORS_SEMANTIC } from '../../styles/emotion/colors3';
import { SectionMessageV2Type } from './types';
import {
  Container,
  IconContainer,
  TagContainer,
  ContentContainer,
  Title,
  Description,
  StyledLink,
  StyledButton,
  StyledXIcon,
} from './SectionMessageV2.styles';

type SectionMessageV2WithHref = {
  /** Where the link points to */
  href: string;
};

type SectionMessageV2WithOnClick = {
  /** The function called by the link */
  onClick: () => void;
};

// Need either both (href or onClick) and alt, or neither
type SectionMessageV2Action = Either<SectionMessageV2WithHref, SectionMessageV2WithOnClick> & {
  /** Text to be displayed for the link */
  alt: string;
};

type CustomIconType = {
  component: React.FC<CreateIconComponentProps>;
  className?: string;
};

type SectionMessageV2CommonProps = {
  /** Classname applied to container */
  className?: string;
  /** Custom icon; overrides the icon associated with a section message type */
  customIcon?: CustomIconType;
  /** Main body for the message */
  description?: ReactNode;
  /** Determines whether the icon should be hidden */
  hideIcon?: boolean;
  /** Handler for when message is dismissed; e.g. allows you update local storage to make dismissal persist */
  onClose?: () => void;
  /** Determines whether to show the close icon that'll allow you to dismiss the message */
  showCloseIcon?: boolean;
  /** Optional tag component to be shown at the top of the section message */
  tagComponent?: JSX.Element;
  /** Title for the message */
  title: string;
  /** Determines color and icon, one of `'confirmation'`, `'warning'`, `'error'`, or `'information'` */
  type?: SectionMessageV2Type;
};

export type SectionMessageV2Props = SectionMessageV2CommonProps &
  ExclusiveAnd<SectionMessageV2Action>;

/**
 * ```import { SectionMessageV2 } from '@zola/zola-ui/src/components/SectionMessageV2';```
 *
 * Provides cue that something has happened in a section of the screen with a title (opt), body text, and link (opt).
 * Users can dismiss the message as well.
 */
const SectionMessageV2: React.FC<SectionMessageV2Props> = ({
  alt,
  className,
  customIcon,
  description,
  hideIcon,
  href,
  onClose = () => undefined,
  showCloseIcon = false,
  tagComponent,
  title,
  type,
  onClick,
}) => {
  const [closed, setClosed] = useState(false);

  const getIconComponent = (type?: SectionMessageV2Type) => {
    const size = 24;
    if (customIcon) {
      const { component: CustomIconComponent, className } = customIcon;
      return (
        <CustomIconComponent
          className={className}
          color={COLORS_SEMANTIC.INFORMATIONAL_100}
          height={size}
          width={size}
        />
      );
    }

    switch (type) {
      case 'confirmation':
        return <CircleCheckIcon color={COLORS_SEMANTIC.POSITIVE_100} height={size} width={size} />;
      case 'warning':
        return <AlertTriangleIcon color={COLORS_SEMANTIC.WARNING_100} height={size} width={size} />;
      case 'error':
        return <CircleXIcon color={COLORS_SEMANTIC.NEGATIVE_100} height={size} width={size} />;
      default:
        return (
          <InfoCircleIcon color={COLORS_SEMANTIC.INFORMATIONAL_100} height={size} width={size} />
        );
    }
  };

  const handleClose = () => {
    setClosed(true);
    onClose();
  };

  const getLinkProps = () => {
    if (href) {
      return { href };
    }
    if (onClick) {
      return { onClick };
    }
    return undefined;
  };

  if (closed) <></>;
  return (
    <Container className={className} type={type}>
      <ContentContainer>
        {!hideIcon ? <IconContainer>{getIconComponent(type)}</IconContainer> : null}
        <div>
          {tagComponent ? <TagContainer>{tagComponent}</TagContainer> : null}
          <Title>{title}</Title>
          {description ? (
            <Description hasLink={Boolean(href || onClick)}>{description}</Description>
          ) : null}
          {Boolean(href || onClick) && (
            <StyledLink inline sizes="small" {...getLinkProps()}>
              {alt}
            </StyledLink>
          )}
        </div>
      </ContentContainer>
      {showCloseIcon ? (
        <StyledButton onClick={handleClose} variant="transparent">
          <StyledXIcon height={20} width={20} />
        </StyledButton>
      ) : null}
    </Container>
  );
};

export default SectionMessageV2;
